import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Icon } from '../components/Icon';
import { apiClient } from '../api/client';
import { useAppStore } from '../store/useAppStore';
import { logger } from '../utils/logger';
import './ExportHistoryScreen.css';
import { useTranslation } from 'react-i18next';

interface ExportRecord {
  id: number;
  filename: string;
  period_text: string;
  export_config: any;
  file_size_kb: number | null;
  created_at: string;
}

export const ExportHistoryScreen: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [history, setHistory] = useState<ExportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [reExporting, setReExporting] = useState<number | null>(null);
  const [downloading, setDownloading] = useState<number | null>(null);
  const { settings } = useAppStore();
  const { t } = useTranslation();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getExportHistory(50);
      setHistory(response.history || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleReExport = async (record: ExportRecord) => {
    try {
      setReExporting(record.id);
      
      const config = record.export_config;
      
      // Формируем параметры для API
      let startDate: string | undefined;
      let endDate: string | undefined;

      if (config.periodType === 'custom' && config.startDate && config.endDate) {
        startDate = config.startDate;
        endDate = config.endDate;
      } else if (config.periodType === 'month' && config.selectedMonth && config.selectedYear) {
        startDate = new Date(config.selectedYear, config.selectedMonth - 1, 1).toISOString().split('T')[0];
        endDate = new Date(config.selectedYear, config.selectedMonth, 0).toISOString().split('T')[0];
      } else if (config.periodType === 'quarter' && config.selectedQuarter && config.selectedYear) {
        const startMonth = (config.selectedQuarter - 1) * 3;
        startDate = new Date(config.selectedYear, startMonth, 1).toISOString().split('T')[0];
        endDate = new Date(config.selectedYear, startMonth + 3, 0).toISOString().split('T')[0];
      } else if (config.periodType === 'year' && config.selectedYear) {
        startDate = new Date(config.selectedYear, 0, 1).toISOString().split('T')[0];
        endDate = new Date(config.selectedYear, 11, 31).toISOString().split('T')[0];
      }

      // Получаем данные транзакций
      const transactionsData = await apiClient.getTransactions(
        config.periodType === 'all' ? 'all' : undefined,
        startDate,
        endDate
      );

      // Получаем цели если нужно
      let goals;
      if (config.includeGoals) {
        const goalsData = await apiClient.getGoals();
        goals = goalsData.goals;
      }

      // Получаем имя пользователя
      const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
      const userName = tgUser?.first_name || tgUser?.username || 'User';

      // Генерируем Excel файл
      const excelBlob = await generateExcelFile(
        {
          transactions: transactionsData.transactions || [],
          goals: goals,
          periodText: record.period_text,
          currency: settings?.currency || 'RUB',
          userName: userName
        },
        config
      );

      const filename = `ALVEON_Export_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Отправляем файл на сервер
      const formData = new FormData();
      formData.append('excel_file', excelBlob, filename);
      formData.append('filename', filename);
      formData.append('period_text', record.period_text);
      formData.append('export_config', JSON.stringify(config));
      formData.append('file_size_kb', Math.round(excelBlob.size / 1024).toString());

      await apiClient.uploadExport(formData);

      // Тактильная обратная связь
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }

      // Обновляем список истории
      await loadHistory();

      
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert(t('export.exportCompletedMessage'));
      }
    } catch (error) {
      
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
      
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert(t('export.exportError'));
      }
    } finally {
      setReExporting(null);
    }
  };

  const handleDownload = async (record: ExportRecord) => {
    const tg = window.Telegram?.WebApp;
    
    try {
      setDownloading(record.id);
      
      // Формируем URL для скачивания
      const downloadUrl = await apiClient.getExportDownloadUrl(record.id);
      logger.debug('Download URL:', downloadUrl.replace(/token=[^&]+/, 'token=***'));
      
      // Тактильная обратная связь
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success');
      }
      
      // ПРАВИЛЬНЫЙ СПОСОБ для Telegram Mini Apps:
      // Открываем прямую ссылку на файл через внешний браузер
      if (tg && tg.openLink) {
        logger.info('Opening file via tg.openLink() - reliable method for Telegram Mini Apps');
        
        // Этот метод работает на всех платформах (iOS, Android, Desktop)
        tg.openLink(downloadUrl);
        
        // Показываем инструкцию
        try {
          tg.showPopup({
            title: t('exportHistory.downloadStartedTitle'),
            message: t('exportHistory.downloadStartedMessage'),
            buttons: [{ id: 'ok', type: 'default', text: t('exportHistory.downloadStartedOk') }]
          });
        } catch (popupError: any) {
          // Игнорируем ошибку если popup уже открыт
          if (popupError?.message !== 'WebAppPopupOpened') {
            logger.error('Popup error:', popupError);
          }
        }
      } else {
        // Fallback для обычного браузера (не Telegram)
        logger.info('Fallback: opening in new tab');
        window.open(downloadUrl, '_blank');
      }
      
    } catch (error: any) {
      logger.error('Download error:', error);
      
      // Показываем ошибку пользователю
      if (tg) {
        try {
          const errorMessage = error?.message || 'Unknown error';
          tg.showAlert(t('exportHistory.downloadErrorDetailed', { message: errorMessage }));
        } catch (popupError: any) {
          logger.error('Cannot show error popup:', popupError);
        }
      }
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (recordId: number) => {
    if (!window.confirm(t('exportHistory.deleteConfirm'))) return;

    try {
      await apiClient.deleteExportHistoryRecord(recordId);
      setHistory(history.filter(r => r.id !== recordId));
      
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      alert(t('exportHistory.deleteError'));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExportTypeText = (config: any) => {
    const types: string[] = [];
    if (config.includeExpenses) types.push(t('export.expenses'));
    if (config.includeIncomes) types.push(t('export.incomes'));
    if (config.includeGoals) types.push(t('export.goals'));
    return types.length > 0 ? types.join(', ') : t('common.noData');
  };

  // Получить информацию о формате файла
  const getFormatInfo = (record: ExportRecord): { icon: string; color: string; label: string } => {
    const configFormat = record.export_config?.format;
    const filename = record.filename?.toLowerCase() || '';
    
    if (configFormat === 'csv' || filename.endsWith('.csv')) {
      return { icon: 'file-text', color: '#FF9500', label: 'CSV' };
    }
    if (configFormat === 'pdf' || filename.endsWith('.pdf')) {
      return { icon: 'file-text', color: '#FF3B30', label: 'PDF' };
    }
    return { icon: 'file-text', color: '#217346', label: 'XLSX' };
  };

  return (
    <div className="export-history-modal">
      <div className="export-history-modal__overlay" onClick={onClose} />
      <div className="export-history-content" onClick={(e) => e.stopPropagation()}>
        <div className="export-history-header">
          <button className="export-history-close" onClick={onClose}>
            <Icon name="close" size={24} />
          </button>
          <h2 className="export-history-title">
            <Icon name="history" size={28} />
            {t('exportHistory.title')}
          </h2>
          <p className="export-history-subtitle">
            {t('exportHistory.subtitle')}
          </p>
        </div>

        <div className="export-history-list">
          {loading ? (
            <div className="export-history-loading">
              <div className="loader"></div>
              <p>{t('exportHistory.loading')}</p>
            </div>
          ) : history.length === 0 ? (
            <div className="export-history-empty">
              <Icon name="file-text" size={64} color="var(--color-text-tertiary)" />
              <h3>{t('exportHistory.emptyTitle')}</h3>
              <p>{t('exportHistory.emptyDescription')}</p>
            </div>
          ) : (
            history.map((record) => {
              const formatInfo = getFormatInfo(record);
              return (
              <Card key={record.id} padding="medium" className="export-record-card neo-static">
                <div className="export-record-header">
                  <div className="export-record-icon" style={{ backgroundColor: `${formatInfo.color}15` }}>
                    <Icon name={formatInfo.icon} size={24} color={formatInfo.color} />
                  </div>
                  <div className="export-record-info">
                    <div className="export-record-period">{record.period_text}</div>
                    <div className="export-record-type">{getExportTypeText(record.export_config)}</div>
                    <div className="export-record-date">
                      {formatDate(record.created_at)}
                      <span className="export-record-format" style={{ color: formatInfo.color, marginLeft: '8px', fontWeight: 600 }}>
                        {formatInfo.label}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="export-record-actions">
                  <button
                    className="export-record-action export-record-action--primary"
                    onClick={() => handleDownload(record)}
                    disabled={downloading === record.id}
                    title={t('exportHistory.downloadTooltip')}
                  >
                    {downloading === record.id ? (
                      <div className="spinner-small"></div>
                    ) : (
                      <Icon name="download" size={14} />
                    )}
                  </button>
                  <button
                    className="export-record-action export-record-action--danger"
                    onClick={() => handleDelete(record.id)}
                    disabled={downloading === record.id}
                    title={t('exportHistory.deleteTooltip')}
                  >
                    <Icon name="trash" size={14} />
                  </button>
                </div>
              </Card>
            )})
          )}
        </div>

        <div className="export-history-footer">
          <button className="neo-raised" onClick={onClose}>
            {t('exportHistory.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

